/**
 * Feature E04-F01 — Inbox pure model/logic (E04-F01-T04 / T05 / T07).
 *
 * Framework-free, side-effect-free helpers ported 1:1 from the legacy SPA
 * `../EER app/public/assets/js/inbox.js` (renderInbox / _filterEmailsBySearch /
 * loadEmails pagination / _buildEmailPreviewHtml attachments). Keeping the logic
 * here makes the pagination/search/classification/attachment rules unit-testable
 * without a DOM, exactly like the project's other feature models.
 *
 * Parity anchors (inbox.js):
 *   - EMAILS_PAGE_SIZE = 50;
 *   - loadEmails(filter, append): resets/appends state.emails, tracks
 *     _emailsOffset (+= number received) and _emailsHasMore (received >= 50);
 *   - the inbox shows ONLY incoming mail — direction==='out' is filtered out
 *     client-side (both on load AND in search);
 *   - search haystack = sender_name/sender/recipient/subject/body_preview/body/
 *     client_name/project_role joined + lowercased `includes`;
 *   - classification badge priority: linked (project_id) > AI classification;
 *   - attachment size: ≤1024KB → "N KB", else "(N/1024).toFixed(1) MB".
 */
import type { EmailDTO } from '@eer/shared';

/** Legacy `app.EMAILS_PAGE_SIZE`. */
export const EMAILS_PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// escaping / sanitizing (legacy `escapeHtml` + the preview HTML strip)
// ---------------------------------------------------------------------------

/** HTML-escape a value (legacy global `escapeHtml`). `null`/`undefined` → ''. */
export function escapeHtml(value: string | null | undefined): string {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Strip HTML tags + collapse whitespace from a body preview (legacy
 * `(email.body_preview||'').replace(/<[^>]*>/g,' ').replace(/\s+/g,' ').trim()`).
 * Used for the list-card preview line — the React component still renders it as
 * text (escaped by React), so this is purely the de-HTML / whitespace pass.
 */
export function stripHtmlPreview(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// T04 — classification badge (priority: linked > AI classification)
// ---------------------------------------------------------------------------

/** Visual badge variants the Inbox card renders. */
export type InboxBadgeTone = 'linked' | 'new-project' | 'muted';

export interface InboxBadge {
  /** Human label, e.g. 'Linked' / 'New Project' / 'Unclassified'. Empty → no badge. */
  readonly label: string;
  /** Visual tone: green (linked) / amber (new project) / gray (everything else). */
  readonly tone: InboxBadgeTone;
}

/**
 * Compute the classification badge for an email (legacy `renderInbox`):
 *   - project_id present → 'Linked' (green) — actual linkage beats AI;
 *   - classification 'new_project_candidate' → 'New Project' (amber);
 *   - 'unclassified' → 'Unclassified' (gray);
 *   - any other non-empty classification → that value verbatim (gray);
 *   - nothing → empty label (no badge rendered).
 */
export function classificationBadge(email: Pick<EmailDTO, 'project_id' | 'meta'>): InboxBadge {
  if (email.project_id != null) {
    return { label: 'Linked', tone: 'linked' };
  }
  const classification = (email.meta?.classification ?? '') as string;
  if (classification === 'new_project_candidate') {
    return { label: 'New Project', tone: 'new-project' };
  }
  if (classification === 'unclassified') {
    return { label: 'Unclassified', tone: 'muted' };
  }
  return { label: classification, tone: 'muted' };
}

// ---------------------------------------------------------------------------
// T05 — pagination (loadEmails offset / hasMore) + search filter
// ---------------------------------------------------------------------------

/** Whether more pages may exist after receiving `received` rows (>= page size). */
export function computeHasMore(received: number, pageSize: number = EMAILS_PAGE_SIZE): boolean {
  return received >= pageSize;
}

/** Next offset = current offset + number of rows actually received (legacy parity). */
export function nextOffset(currentOffset: number, received: number): number {
  return currentOffset + received;
}

/**
 * Drop outgoing mail — the Inbox feed shows ONLY incoming emails
 * (`direction !== 'out'`). Applied on load AND in the client search filter.
 */
export function incomingOnly(emails: readonly EmailDTO[]): EmailDTO[] {
  return emails.filter((e) => e.direction !== 'out');
}

/** Lowercased search haystack for one email (legacy `_filterEmailsBySearch`). */
export function searchHaystack(email: EmailDTO): string {
  return [
    email.sender_name,
    email.sender,
    email.recipient,
    email.subject,
    email.body_preview,
    email.body,
    email.client_name,
    email.project_role,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Client-side search: case-insensitive `includes` over the joined haystack, and
 * (legacy parity) ALWAYS drops outgoing mail. An empty/blank query returns the
 * incoming-only list unchanged.
 */
export function filterEmailsBySearch(emails: readonly EmailDTO[], query: string): EmailDTO[] {
  const incoming = incomingOnly(emails);
  const q = (query ?? '').toLowerCase().trim();
  if (!q) return incoming;
  return incoming.filter((e) => searchHaystack(e).includes(q));
}

// ---------------------------------------------------------------------------
// classification filter tabs (legacy `#inbox-filters .inbox-filter`)
// ---------------------------------------------------------------------------

export interface InboxFilterTab {
  /** Value sent as `classification` to GET /emails ('all' → omitted). */
  readonly value: string;
  /** Tab label. */
  readonly label: string;
}

/**
 * The legacy inbox filter tabs (data-filter values), in the exact v1 order/labels
 * (legacy `index.php` #inbox-filters, FP-08 parity): All / Unclassified /
 * New Projects / Linked. `value`s are unchanged (drive the `inbox-filter-<value>`
 * testids + the GET /emails `classification` param) — only order and the
 * `new_project_candidate` label ('New Project' → 'New Projects') follow v1.
 */
export const INBOX_FILTER_TABS: readonly InboxFilterTab[] = [
  { value: 'all', label: 'All' },
  { value: 'unclassified', label: 'Unclassified' },
  { value: 'new_project_candidate', label: 'New Projects' },
  { value: 'existing_project', label: 'Linked' },
];

// ---------------------------------------------------------------------------
// T07 — attachments (size formatting + icon by content type)
// ---------------------------------------------------------------------------

export interface AttachmentLike {
  readonly filename: string;
  readonly contentType?: string;
  readonly size?: number;
}

/**
 * Format an attachment size (legacy `_buildEmailPreviewHtml`):
 *   sizeKB = round(size/1024); sizeKB > 1024 → "(sizeKB/1024).toFixed(1) MB",
 *   else "sizeKB KB". A missing size → "0 KB".
 */
export function formatAttachmentSize(sizeBytes: number | undefined): string {
  const sizeKB = sizeBytes ? Math.round(sizeBytes / 1024) : 0;
  return sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`;
}

/**
 * Font Awesome icon class for an attachment content type (legacy mapping):
 *   pdf → fa-file-pdf; word/document → fa-file-word; sheet/excel → fa-file-excel;
 *   image → fa-file-image; otherwise → fa-file.
 */
export function attachmentIcon(contentType: string | undefined): string {
  const ct = contentType ?? '';
  if (ct.includes('pdf')) return 'fa-file-pdf text-red-400';
  if (/word|document/.test(ct)) return 'fa-file-word text-blue-400';
  if (/sheet|excel/.test(ct)) return 'fa-file-excel text-green-400';
  if (ct.includes('image')) return 'fa-file-image text-purple-400';
  return 'fa-file text-gray-400';
}

/** Read an email's attachments array off `meta` (always an array). */
export function emailAttachments(email: Pick<EmailDTO, 'meta'>): AttachmentLike[] {
  const list = email.meta?.attachments;
  return Array.isArray(list) ? (list as AttachmentLike[]) : [];
}

/** True when the email carries at least one attachment (paperclip in the card). */
export function hasAttachments(email: Pick<EmailDTO, 'meta'>): boolean {
  return emailAttachments(email).length > 0;
}

// ---------------------------------------------------------------------------
// T04 — preview display helpers
// ---------------------------------------------------------------------------

/** "Name <email>" or just the email when no display name (legacy senderDisplay). */
export function senderDisplay(email: Pick<EmailDTO, 'sender' | 'sender_name'>): string {
  if (email.sender_name) {
    return `${email.sender_name} <${email.sender ?? ''}>`;
  }
  return email.sender ?? '';
}

/** Card sender line: sender_name || sender || 'Unknown'. */
export function cardSender(email: Pick<EmailDTO, 'sender' | 'sender_name'>): string {
  return email.sender_name || email.sender || 'Unknown';
}

/** "To:" recipient — recipient || meta.account_email || '—' (legacy preview). */
export function previewRecipient(email: Pick<EmailDTO, 'recipient' | 'meta'>): string {
  return email.recipient || (email.meta?.account_email as string | undefined) || '—';
}

/** Subject with the legacy fallback '(no subject)'. */
export function subjectLabel(email: Pick<EmailDTO, 'subject'>): string {
  return email.subject || '(no subject)';
}

/**
 * Body lines for the preview (legacy `body.replace(/\n/g,'<br>')`). Returns the
 * split lines so the React component renders them with <br/> between (escaped by
 * React). Falls back to the preview when the full body has not loaded yet.
 */
export function previewBodyLines(email: Pick<EmailDTO, 'body' | 'body_preview'>): string[] {
  const text = email.body || email.body_preview || '';
  return text.split('\n');
}

/** True while the full body is still being fetched (preview shows the spinner). */
export function isBodyLoading(email: Pick<EmailDTO, 'body' | 'body_preview'>): boolean {
  return !email.body && !!email.body_preview;
}

// ---------------------------------------------------------------------------
// date formatting (legacy parity — the card lost time in the v2 port)
// ---------------------------------------------------------------------------

/**
 * Short card date WITH time — parity with legacy `renderInbox`:
 *   `toLocaleDateString('en-US',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})`
 * e.g. "01 Jun, 09:30". A null/invalid timestamp → ''.
 */
export function formatCardDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/**
 * Full preview date+time — parity with legacy `_buildEmailPreviewHtml`:
 * `new Date(received_at).toLocaleString('en-US')`. A null/invalid timestamp → ''.
 */
export function formatPreviewDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US');
}
